// src/pages/CompareAreas.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./CompareAreas.css";

import axios from "axios"; // if you're using axios

// Round 6 (2026-05-05): API_BASE resolution standardized to match aiApi.js +
// siteFeedbackApi.js. Was previously falling back to API_BASE1 from config.js
// (which points at production Render), so local-dev compare calls hit prod
// → CORS / network failure → "Failed to fetch". Now falls back to localhost
// for dev; production gets REACT_APP_API_BASE injected on Vercel.
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5050";

// Tier 2.2 (2026-05-06): debounce keystrokes before hitting Google Places
// autocomplete to keep request volume sane. 300ms is the standard UX sweet
// spot — fast enough to feel live, slow enough that mid-word typing doesn't
// fire a request per character.
const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const AUTOCOMPLETE_MIN_CHARS = 3;

/**
 * CompareAreas — neighborhood comparison view.
 *
 * Supports two render modes (Round 6, 2026-05-05 LG feedback):
 *
 * 1) Standalone page (default) — accessed via /areas/:slug/compare deep link.
 *    Reads item/city/heroImage from useLocation().state and useParams.slug.
 *    Renders the topbar with back button + chat-return button.
 *
 * 2) Embedded mode — used inside AIChat's right panel. The parent passes
 *    `embedded={true}` plus `propsItem`, `propsCity`, `onClose`. The internal
 *    topbar is hidden (AIChat provides its own "← Back to results" header).
 *    Back action invokes onClose() instead of navigate(-1).
 */
export default function CompareAreas({
  embedded = false,
  propsItem = null,
  propsCity = null,
  propsHeroImage = null,
  onClose = null,
} = {}) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();

  // Resolve data source: embedded mode prefers props, standalone uses router state.
  const selectedItem = embedded ? (propsItem || null) : (state?.item || null);
  const selectedImage = embedded
    ? (propsHeroImage || propsItem?.image || null)
    : (selectedItem?.image || selectedItem?.img || selectedItem?.photo || null);

  const area1 = embedded
    ? (propsItem?.title || propsItem?.name || "Area")
    : (state?.item?.title || (slug ? slug.replace(/-/g, " ") : "Santa Monica"));
  const city = embedded
    ? (propsCity || propsItem?.city || "Los Angeles")
    : (state?.city || "Los Angeles");

  // Tier 2.2 (2026-05-06): split typing state from committed compare value.
  // Previously every keystroke fired the GPT-backed /api/areas/compare call.
  // Now `area2Input` tracks what the user is typing (drives autocomplete +
  // dropdown), and `area2` (the committed value) only changes when the user
  // explicitly picks a suggestion, hits Enter, or blurs with non-empty text.
  // Compare keeps its existing useEffect dep on `area2`, so behavior is
  // unchanged from the moment of commit onward.
  const [area2Input, setArea2Input] = useState("");
  const [area2, setArea2] = useState(""); // leave empty to let server pick a random contrasting area
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1); // for keyboard nav
  // Visible diagnostic so a backend-misconfig shows up in the UI instead
  // of silently failing. Cleared when a fetch succeeds.
  const [autocompleteStatus, setAutocompleteStatus] = useState(""); // "" | "loading" | "404" | "503" | "502" | "error"
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Debounced autocomplete fetcher. Cleans up the timer on each keystroke
  // so only the last "settled" input fires a request.
  useEffect(() => {
    if (area2Input.trim().length < AUTOCOMPLETE_MIN_CHARS) {
      setSuggestions([]);
      setAutocompleteStatus("");
      return undefined;
    }
    // Skip fetch when the typed input matches the already-committed value
    // (e.g. immediately after picking a suggestion). Prevents a wasted call
    // and a stale dropdown flicker.
    if (area2Input.trim() === area2.trim() && area2.trim() !== "") {
      return undefined;
    }

    const ctrl = new AbortController();
    setAutocompleteStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          input: area2Input.trim(),
          ...(city ? { city } : {}),
        });
        const res = await fetch(
          `${API_BASE}/api/places/autocomplete-neighborhood?${params.toString()}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) {
          // 503 = key not configured; 502 = upstream issue; 404 = route
          // not mounted (backend wasn't restarted). We surface the status
          // both in the UI (small text under input) and in console.warn
          // so the failure mode is visible without DevTools.
          console.warn(
            `[CompareAreas] autocomplete fetch returned ${res.status}. ` +
            (res.status === 404
              ? "Backend may need a restart to mount /api/places."
              : res.status === 503
              ? "GOOGLE_MAPS_API_KEY is missing on the backend."
              : "Upstream error.")
          );
          setSuggestions([]);
          setAutocompleteStatus(String(res.status));
          return;
        }
        const json = await res.json();
        setSuggestions(json.suggestions || []);
        setActiveIndex(-1);
        setAutocompleteStatus("");
      } catch (e) {
        if (e.name !== "AbortError") {
          setSuggestions([]);
          setAutocompleteStatus("error");
        }
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [area2Input, city, area2]);

  // Click-outside closes dropdown
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showDropdown]);

  const commitArea2 = (value) => {
    const clean = (value || "").trim();
    setArea2Input(clean);
    setArea2(clean);
    setShowDropdown(false);
    setActiveIndex(-1);
  };

  const onSelectSuggestion = (sugg) => {
    // Use mainText (just the neighborhood name) for the comparison query;
    // keeps the GPT compare prompt focused without secondary city/state.
    commitArea2(sugg.mainText || sugg.description);
  };

  const onArea2KeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        commitArea2(area2Input);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        onSelectSuggestion(suggestions[activeIndex]);
      } else {
        commitArea2(area2Input);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  // ✅ Persist the hero image & title so AreaDetails / ExploreMore can read it reliably
  useEffect(() => {
    if (selectedImage) localStorage.setItem("lastHeroImage", selectedImage);
    if (area1) localStorage.setItem("lastAreaTitle", area1);
  }, [selectedImage, area1]);

  // (Optional) If you later add buttons here, these helpers already pass the same image via router state
  const goDetails = () =>
    navigate("/area/details", {
      state: {
        id: selectedItem?.id,
        title: area1,
        city,
        image: selectedImage,
      },
    });

  const goExplore = () =>
    navigate("/area/explore", {
      state: {
        id: selectedItem?.id,
        title: area1,
        city,
        image: selectedImage,
      },
    });

  useEffect(() => {
    let ignore = false;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const body = {
          city,
          area1Name: area1,
          // IMPORTANT: if area2 is blank, we omit it so the server chooses a random/contrasting area
          ...(area2.trim() ? { area2Name: area2.trim() } : {})
        };

        const res = await fetch(`${API_BASE}/api/areas/compare`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });

        // If the server returned HTML (like an index.html), show a clearer error
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          throw new Error(
            "Could not parse response from API. Check API URL / proxy. " +
            "Response starts with: " + text.slice(0, 60)
          );
        }

        if (!res.ok) throw new Error(json?.error || "compare failed");
        if (!ignore) setData(json);
      } catch (e) {
        console.error(e);
        if (!ignore) {
          setErr(String(e.message || e));
          setData(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [area1, city, area2]);

  const d = data || {
    title: "Comparison Analysis",
    area1: area1,
    area2: area2 || "Area 2",
    rows: [],
    overall: { area1: 0, area2: 0 },
    recommendation: { name: area1, rationale: "", tags: [] },
    vibe: {}
  };

  // Round 6: when embedded, AIChat owns the back action and provides its own
  // header pill. Standalone mode keeps the original topbar.
  const handleBack = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`cmp-wrap${embedded ? " cmp-wrap-embedded" : ""}`}>
      {/* Top bar — hidden in embedded mode (AIChat provides its own header) */}
      {!embedded && (
        <div className="cmp-topbar">
          <button className="cmp-icon" onClick={handleBack} aria-label="Back">←</button>
          <div className="cmp-title">Comparison Analysis</div>
          <button
            className="cmp-chat"
            onClick={() => navigate("/home")}
            title="Back to chat"
            aria-label="Back to chat"
          >
            💬
          </button>
        </div>
      )}

      {/* Round 6 header — splash eyebrow + headline pattern, applies to both
          standalone and embedded modes (replaces the legacy lowercase header). */}
      <div className="cmp-header">
        <span className="cmp-eyebrow">Side by side</span>
        <h2 className="cmp-headline">
          Compare{' '}
          <span className="cmp-headline-accent">{area1}</span>
          {area2 && (
            <>
              {' '}vs.{' '}
              <span className="cmp-headline-accent">{area2}</span>
            </>
          )}
        </h2>
      </div>

      {/* Picker — Tier 2.2 (2026-05-06): Google Places autocomplete dropdown.
          Wraps input + dropdown in `cmp-autocomplete` so the suggestion list
          can position absolutely under the input. */}
      <div className="cmp-picker">
        <label htmlFor="cmp-area2-input">
          Compare with another area
        </label>
        <div className="cmp-autocomplete">
          <input
            id="cmp-area2-input"
            ref={inputRef}
            className="cmp-input"
            placeholder="(optional) type another area"
            value={area2Input}
            onChange={(e) => {
              setArea2Input(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => {
              if (suggestions.length > 0) setShowDropdown(true);
            }}
            onBlur={() => {
              // Slight delay so click-on-suggestion lands before blur fires.
              // Click handler will commit; otherwise reconcile committed value
              // with what the user has typed:
              //   - cleared input  → commit empty (revert to random compare)
              //   - typed text     → commit as free-text fallback
              setTimeout(() => {
                if (area2Input.trim() !== area2) {
                  commitArea2(area2Input);
                }
              }, 150);
            }}
            onKeyDown={onArea2KeyDown}
            autoComplete="off"
          />
          {showDropdown && area2Input.trim().length >= AUTOCOMPLETE_MIN_CHARS && (
            <ul className="cmp-suggestions" ref={dropdownRef} role="listbox">
              {suggestions.length > 0 ? (
                suggestions.map((s, i) => (
                  <li
                    key={s.place_id || s.description}
                    className={`cmp-suggestion${i === activeIndex ? " active" : ""}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    // onMouseDown (not onClick) fires before input blur,
                    // so the selection lands before the blur handler runs.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelectSuggestion(s);
                    }}
                  >
                    <span className="cmp-suggestion-main">{s.mainText}</span>
                    {s.secondaryText && (
                      <span className="cmp-suggestion-secondary">
                        {' '}{s.secondaryText}
                      </span>
                    )}
                  </li>
                ))
              ) : autocompleteStatus === "loading" ? (
                <li className="cmp-suggestion-empty">Searching…</li>
              ) : autocompleteStatus === "" ? (
                // Empty status + no suggestions = fetch completed with zero
                // results. Surface that explicitly so the user knows the
                // input is working but nothing matched.
                <li className="cmp-suggestion-empty">
                  No matches. Try a different spelling or a broader term.
                </li>
              ) : null}
            </ul>
          )}
        </div>
        {/* Visible diagnostic when autocomplete fails — better than a
            silently empty dropdown. Helps debug "did backend restart?" /
            "is the key configured?" without needing DevTools. */}
        {autocompleteStatus && autocompleteStatus !== "loading" && (
          <div className="cmp-autocomplete-status">
            {autocompleteStatus === "404" &&
              "⚠ Autocomplete endpoint not found — backend needs a restart to load /api/places."}
            {autocompleteStatus === "503" &&
              "⚠ GOOGLE_MAPS_API_KEY missing on backend — autocomplete disabled."}
            {autocompleteStatus === "502" &&
              "⚠ Google Places upstream error — try again in a moment."}
            {autocompleteStatus === "error" &&
              "⚠ Network error reaching autocomplete service."}
            {!["404", "503", "502", "error"].includes(autocompleteStatus) &&
              `⚠ Autocomplete returned status ${autocompleteStatus}.`}
          </div>
        )}
        <div>
          Leave blank to compare with a <b>random contrasting area</b>.
        </div>
      </div>

      {loading && <div className="cmp-note">Analyzing…</div>}
      {err && <div className="cmp-err">{err}</div>}

      {/* Table */}
      <div className="cmp-table">
        <div className="cmp-row cmp-head">
          <div className="cmp-cell">Criteria</div>
          <div className="cmp-cell">{d.area1}</div>
          <div className="cmp-cell">{d.area2}</div>
        </div>

        {(d.rows || []).map((r, i) => (
          <div className={`cmp-row ${i % 2 ? "alt" : ""}`} key={i}>
            <div className="cmp-cell">{r.label}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area1}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area2}</div>
          </div>
        ))}

        <div className="cmp-row alt">
          <div className="cmp-cell">Overall Score</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area1 ?? 0}</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area2 ?? 0}</div>
        </div>

        <div className="cmp-row">
          <div className="cmp-cell">Vibe</div>
          <div className="cmp-cell">{d.vibe?.area1 || "—"}</div>
          <div className="cmp-cell">{d.vibe?.area2 || "—"}</div>
        </div>
      </div>

      {/* Recommendation */}
      <div className="cmp-reco">
        <div className="cmp-reco-head">✅ Recommendation:</div>
        <div className="cmp-reco-name">{d.recommendation?.name || area1}</div>
        <p className="cmp-reco-text">{d.recommendation?.rationale || ""}</p>
        <div className="cmp-tags">
          {(d.recommendation?.tags || []).map((t, i) => (
            <span key={i} className="cmp-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}






/*
// src/pages/CompareAreas.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./CompareAreas.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5050"; // <-- ensures it hits Node API

export default function CompareAreas() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();

  // ✅ selected item from previous page (contains the Unsplash image you showed on the card)
  const selectedItem = state?.item || null;
  const selectedImage =
    selectedItem?.image || selectedItem?.img || selectedItem?.photo || null;

  const area1 = state?.item?.title || (slug ? slug.replace(/-/g, " ") : "Santa Monica");
  const city  = state?.city || "Los Angeles";

  const [area2, setArea2] = useState(""); // leave empty to let server pick a random contrasting area
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ Persist the hero image & title so AreaDetails / ExploreMore can read it reliably
  useEffect(() => {
    if (selectedImage) localStorage.setItem("lastHeroImage", selectedImage);
    if (area1) localStorage.setItem("lastAreaTitle", area1);
  }, [selectedImage, area1]);

  // (Optional) If you later add buttons here, these helpers already pass the same image via router state
  const goDetails = () =>
    navigate("/area/details", {
      state: {
        id: selectedItem?.id,
        title: area1,
        city,
        image: selectedImage,
      },
    });

  const goExplore = () =>
    navigate("/area/explore", {
      state: {
        id: selectedItem?.id,
        title: area1,
        city,
        image: selectedImage,
      },
    });

  useEffect(() => {
    let ignore = false;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const body = {
          city,
          area1Name: area1,
          // IMPORTANT: if area2 is blank, we omit it so the server chooses a random/contrasting area
          ...(area2.trim() ? { area2Name: area2.trim() } : {})
        };

        const res = await fetch(`${API_BASE}/api/areas/compare`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });

        // If the server returned HTML (like an index.html), show a clearer error
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          throw new Error(
            "Could not parse response from API. Check API URL / proxy. " +
            "Response starts with: " + text.slice(0, 60)
          );
        }

        if (!res.ok) throw new Error(json?.error || "compare failed");
        if (!ignore) setData(json);
      } catch (e) {
        console.error(e);
        if (!ignore) {
          setErr(String(e.message || e));
          setData(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [area1, city, area2]);

  const d = data || {
    title: "Comparison Analysis",
    area1: area1,
    area2: area2 || "Area 2",
    rows: [],
    overall: { area1: 0, area2: 0 },
    recommendation: { name: area1, rationale: "", tags: [] },
    vibe: {}
  };

  return (
    <div className="cmp-wrap">
      
      <div className="cmp-topbar">
        <button className="cmp-icon" onClick={() => navigate(-1)} aria-label="Back">←</button>
        <div className="cmp-title">Comparison Analysis</div>
        <div className="cmp-chat">💬</div>
      </div>

     
      <div className="cmp-picker">
        <label>
          Compare <b>{area1}</b> with:
        </label>
        <input
          className="cmp-input"
          placeholder="(optional) type another area"
          value={area2}
          onChange={(e) => setArea2(e.target.value)}
        />
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Leave blank to compare with a <b>random contrasting area</b>.
        </div>
      </div>

      {loading && <div className="cmp-note">Analyzing…</div>}
      {err && <div className="cmp-err">{err}</div>}

      
      <div className="cmp-table">
        <div className="cmp-row cmp-head">
          <div className="cmp-cell">Criteria</div>
          <div className="cmp-cell">{d.area1}</div>
          <div className="cmp-cell">{d.area2}</div>
        </div>

        {(d.rows || []).map((r, i) => (
          <div className={`cmp-row ${i % 2 ? "alt" : ""}`} key={i}>
            <div className="cmp-cell">{r.label}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area1}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area2}</div>
          </div>
        ))}

        <div className="cmp-row alt">
          <div className="cmp-cell">Overall Score</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area1 ?? 0}</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area2 ?? 0}</div>
        </div>

        <div className="cmp-row">
          <div className="cmp-cell">Vibe</div>
          <div className="cmp-cell">{d.vibe?.area1 || "—"}</div>
          <div className="cmp-cell">{d.vibe?.area2 || "—"}</div>
        </div>
      </div>

      
      <div className="cmp-reco">
        <div className="cmp-reco-head">✅ Recommendation:</div>
        <div className="cmp-reco-name">{d.recommendation?.name || area1}</div>
        <p className="cmp-reco-text">{d.recommendation?.rationale || ""}</p>
        <div className="cmp-tags">
          {(d.recommendation?.tags || []).map((t, i) => (
            <span key={i} className="cmp-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
*/



/*
// src/pages/CompareAreas.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./CompareAreas.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5050"; // <-- ensures it hits Node API

export default function CompareAreas() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();

  const area1 = state?.item?.title || (slug ? slug.replace(/-/g, " ") : "Santa Monica");
  const city  = state?.city || "Los Angeles";

  const [area2, setArea2] = useState(""); // leave empty to let server pick a random contrasting area
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let ignore = false;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const body = {
          city,
          area1Name: area1,
          // IMPORTANT: if area2 is blank, we omit it so the server chooses a random/contrasting area
          ...(area2.trim() ? { area2Name: area2.trim() } : {})
        };

        const res = await fetch(`${API_BASE}/api/areas/compare`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });

        // If the server returned HTML (like an index.html), show a clearer error
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          throw new Error(
            "Could not parse response from API. Check API URL / proxy. " +
            "Response starts with: " + text.slice(0, 60)
          );
        }

        if (!res.ok) throw new Error(json?.error || "compare failed");
        if (!ignore) setData(json);
      } catch (e) {
        console.error(e);
        if (!ignore) {
          setErr(String(e.message || e));
          setData(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [area1, city, area2]);

  const d = data || {
    title: "Comparison Analysis",
    area1: area1,
    area2: area2 || "Area 2",
    rows: [],
    overall: { area1: 0, area2: 0 },
    recommendation: { name: area1, rationale: "", tags: [] },
    vibe: {}
  };

  return (
    <div className="cmp-wrap">
    
      <div className="cmp-topbar">
        <button className="cmp-icon" onClick={() => navigate(-1)} aria-label="Back">←</button>
        <div className="cmp-title">Comparison Analysis</div>
        <div className="cmp-chat">💬</div>
      </div>

    
      <div className="cmp-picker">
        <label>
          Compare <b>{area1}</b> with:
        </label>
        <input
          className="cmp-input"
          placeholder="(optional) type another area"
          value={area2}
          onChange={(e) => setArea2(e.target.value)}
        />
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Leave blank to compare with a <b>random contrasting area</b>.
        </div>
      </div>

      {loading && <div className="cmp-note">Analyzing…</div>}
      {err && <div className="cmp-err">{err}</div>}

     
      <div className="cmp-table">
        <div className="cmp-row cmp-head">
          <div className="cmp-cell">Criteria</div>
          <div className="cmp-cell">{d.area1}</div>
          <div className="cmp-cell">{d.area2}</div>
        </div>

        {(d.rows || []).map((r, i) => (
          <div className={`cmp-row ${i % 2 ? "alt" : ""}`} key={i}>
            <div className="cmp-cell">{r.label}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area1}</div>
            <div className="cmp-cell" style={{ whiteSpace: "pre-wrap" }}>{r.area2}</div>
          </div>
        ))}

        <div className="cmp-row alt">
          <div className="cmp-cell">Overall Score</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area1 ?? 0}</div>
          <div className="cmp-cell"><span className="star">★</span> {d.overall?.area2 ?? 0}</div>
        </div>

        <div className="cmp-row">
          <div className="cmp-cell">Vibe</div>
          <div className="cmp-cell">{d.vibe?.area1 || "—"}</div>
          <div className="cmp-cell">{d.vibe?.area2 || "—"}</div>
        </div>
      </div>

     
      <div className="cmp-reco">
        <div className="cmp-reco-head">✅ Recommendation:</div>
        <div className="cmp-reco-name">{d.recommendation?.name || area1}</div>
        <p className="cmp-reco-text">{d.recommendation?.rationale || ""}</p>
        <div className="cmp-tags">
          {(d.recommendation?.tags || []).map((t, i) => (
            <span key={i} className="cmp-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
*/